'use client'

import { X } from 'lucide-react'
import Image from 'next/image'
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Player } from '@repo/types'; 
import { getDriverBackgroundColor, toMillionPrice, formatPrice } from '../../lib/utils';
import { useCompetitionContext } from "../../providers/CompetitionContext";

interface PlayerInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRemove: () => void;
  driver: Player;
  weekId: number;
}

interface StatRowProps {
  label: string;
  position?: string;
  value: string | number;
}

interface TableHeaderProps {
  tab: string;
}

function StatRow({ label, position, value }: StatRowProps) {
  return (
    <div className="grid grid-cols-3 py-4 px-6 hover:bg-gray-50">
      <div className="text-navy-900 font-medium">{label}</div>
      <div className="text-right font-bold text-navy-900">{position}</div>
      <div className="text-right font-bold text-navy-900">{value}</div>
    </div>
  );
}

// Header component for the tables
function TableHeader({ tab }: TableHeaderProps) {
  return (
    <div className="grid grid-cols-3 py-3 px-6 bg-red-600 text-white font-bold">
      <div>Onderdeel</div>
      {(tab !== 'season') && <div className="text-right">Positie</div>}
      {(tab !== 'season') && <div className="text-right">Punten</div>}
    </div>
  );
}

export function PlayerInfoModal({ isOpen, onClose, onRemove, driver, weekId }: PlayerInfoModalProps) {
  const { brand, competitionData } = useCompetitionContext();

  if (!isOpen) return null;

  const club = competitionData.clubs.find((club: any) => club.id === driver.clubId);

  const viewWeek = weekId >= competitionData?.deadlineInfo?.deadlineWeek ? competitionData?.deadlineInfo?.deadlineWeek-1 : weekId;
  const raceName = competitionData.matches.find((m:any) => m.weekId === viewWeek);

  // Filter stats based on the week
  const driverStat = competitionData.stats.find((s: any) => {
    // Parse the JSON string to access driver_id
    const parsedStat = JSON.parse(s.r || '{}');
    return parsedStat.driver_id === driver.externalId && s.w === viewWeek;
  });
  
  const stats = driverStat ? (JSON.parse(driverStat?.r)) : null;

  const raceStats = [
    { label: "Kwalificatie", position: stats?.qualifying_position, value: stats?.qualifying_points },
    { label: "Race", position: stats?.race_position, value: stats?.race_points },
  ];

  if(stats?.sprint_qualifying_position || stats?.sprint_position) {
    raceStats.unshift(
      { label: "Sprintkwalificatie", position: stats?.sprint_qualifying_position, value: stats?.sprint_qualifying_points },
      { label: "Sprintrace", position: stats?.sprint_position, value: stats?.sprint_points },
    )
  }

  // Get all season stats for this driver based on externalId
  const seasonStatsData = competitionData.stats.filter((s: any) => {
    const parsedStat = JSON.parse(s.r || '{}');
    return parsedStat.driver_id === driver.externalId;
  });
  
  // Process the stats to get the required values
  const processedStats = seasonStatsData.map((stat: any) => {
    const parsedStat = JSON.parse(stat?.r || '{}');
    return {
      racePosition: parsedStat?.race_position,
      qualifyingPosition: parsedStat?.qualifying_position,
      points: stat.p
    };
  }).filter((stat: any) => stat.racePosition !== undefined);
  
  // Calculate the best finish and count occurrences
  const racePositions = processedStats.map((stat: any) => stat.racePosition).filter((pos:any) => pos !== undefined);
  const bestRacePosition = Math.min(...racePositions.map((pos:any) => Number(pos) || Infinity));
  const bestRacePositionCount = racePositions.filter((pos:any) => pos === bestRacePosition).length;
  
  // Calculate the best qualifying and count occurrences
  const qualifyingPositions = processedStats.map((stat: any) => stat.qualifyingPosition).filter((pos:any) => pos !== undefined);
  const bestQualifyingPosition = Math.min(...qualifyingPositions.map((pos:any) => Number(pos) || Infinity));
  const bestQualifyingPositionCount = qualifyingPositions.filter((pos:any) => pos === bestQualifyingPosition).length;
  
  // Calculate other stats
  const victories = racePositions.filter((pos:any) => pos === 1).length;
  const podiums = racePositions.filter((pos:any) => pos <= 3).length;
  const totalPoints = seasonStatsData.reduce((sum: any, stat: any) => sum + (stat.p || 0), 0);
  const polePositions = qualifyingPositions.filter((pos:any) => pos === 1).length;
  
  const seasonStats = [
    { label: "Races", value: processedStats.length },
    { label: "Beste finish", value: `${bestRacePosition}${bestRacePositionCount > 1 ? ` (x${bestRacePositionCount})` : ''}` },
    { label: "Beste kwalificatie", value: `${bestQualifyingPosition}${bestQualifyingPositionCount > 1 ? ` (x${bestQualifyingPositionCount})` : ''}` },
    { label: "Overwinningen", value: victories },
    { label: "Podiums", value: podiums },
    { label: "Punten", value: totalPoints },
    { label: "Pole positions", value: polePositions },
  ];

  const careerStats = [
    { label: "Races", value: "185" },
    { label: "Overwinningen", value: "54" },
    { label: "Podiums", value: "98" },
    { label: "Wereldtitels", value: "3" },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className={`${getDriverBackgroundColor(club.id)} text-white p-6 relative`}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
          >
            <X size={24} />
          </button>
          
          <div className={`flex items-center gap-6`}>
            <Image
              src={`https://dpgnl.s3.eu-west-1.amazonaws.com/jerseys/formulaone/${driver.id}.png`}
              alt={driver.name}
              width={40}
              height={40}
              className="w-40 rounded-lg"
            />
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold">{driver.name}</h2>
              </div>
              <p className="text-gray-200">{club.name}</p>
            </div>
          </div>
        </div>

        {/* Remove Button */}
        {(weekId >= competitionData.deadlineInfo.deadlineWeek) && <div className="bg-gray-100 py-4">
          <button
            onClick={onRemove}
            className={`mx-auto block bg-red-700 text-white px-8 py-2 rounded-full font-bold hover:bg-red-500 transition-colors`}
          >
            VERWIJDER
          </button>
        </div>}

        <Tabs defaultValue="race" className="w-full">
          <TabsList className="w-full grid grid-cols-2 bg-transparent border-b">
            <TabsTrigger 
              value="race"
              className="data-[state=active]:border-b-2 data-[state=active]:border-red-600 data-[state=active]:shadow-none rounded-none"
            >
              {raceName?.feedUrl || 'Race'} GP
            </TabsTrigger>
            <TabsTrigger 
              value="season"
              className="data-[state=active]:border-b-2 data-[state=active]:border-red-600 data-[state=active]:shadow-none rounded-none"
            >
              Seizoen
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="race" className="mt-0">
            <div>
              <TableHeader tab='race' />
              <div className="divide-y">
                {raceStats.map((stat) => (
                  <StatRow key={stat.label} {...stat} />
                ))}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="season" className="mt-0">
            <div>
              <TableHeader tab='season' />
              <div className="divide-y">
                {seasonStats.map((stat) => (
                  <StatRow key={stat.label} {...stat} />
                ))}
              </div>
            </div>
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
}